import photoView from '../../photoView/index.js';

export default (route, params, options) => ({
    ...photoView(route, params, options),

    removedCount: 0,
    _suppressGridReload: false,

    options: {
        ...{
            preview: true,
            showHighResLink: true,
            tags: false,
            sort: 'name',
            gridContainerId: 'gridContainerPhotoView',
            selectable: true,
            showHighRes: true,
            removed: false,
        },
        ...options,
    },

    init() {
        this.reset();
        this.start();

        this.initWatches();
        this.initWindowListeners();
    },

    initWatches() {
        this.$watch('tilesState', () => {
            if (this.allSelected) {
                this.selectedTiles = [];
            }
        });
    },

    initWindowListeners() {
        const onConfirmPhotos = () => this.listenerAddPhotoEvent();

        const onToggleRemoved = (event) => {
            this.options.removed = event.removed;
            this.reset();
            this.start();
        };

        const onGridReload = () => {
            if (this._suppressGridReload) {
                this._suppressGridReload = false;
                return;
            }
            this.reset();
            this.start();
        };

        window.addEventListener('confirm-photos', onConfirmPhotos);
        const offToggleRemoved = window.Livewire.on('quality-control::toggle-removed', onToggleRemoved);
        const offGridReload = window.Livewire.on('quality-control::grid-reload', onGridReload);

        this.$cleanup(() => {
            window.removeEventListener('confirm-photos', onConfirmPhotos);
            offToggleRemoved();
            offGridReload();
        });
    },

    /**
     * Called after fillTilesState resolves (i.e. after real tile data arrives for a section).
     * Applies any active shift-select id-ranges to newly loaded tiles without touching tilesState
     * inside the $watch callback (which would cause recursive watcher invocations).
     */
    syncRangesToLoadedTiles(tiles) {
        if (!this.selectionIdRanges.length) {
            return;
        }

        Object.entries(tiles).forEach(([index, tile]) => {
            if (!tile?.id || tile.selected) return;

            const isOnRange = this.selectionIdRanges.some(
                (range) => range.start_id <= tile.id && range.end_id >= tile.id,
            );

            if (isOnRange && !this.unselectedAfterShiftSelect.includes(tile.id)) {
                tile.selected = true;
                this.updateTileVisualState(index, true);
            }
        });
    },

    createGridScroll() {
        const existingNode = document.getElementById('photo-view-grid-scroll' + this.container.id);
        existingNode?.remove();

        this.gridScrollNode = document.createElement('div');
        this.gridScrollNode.id = 'photo-view-grid-scroll' + this.container.id;

        this.container.appendChild(this.gridScrollNode);
    },

    async loadInitialScreen() {
        const { groups, total, removed_count } = await this.getSectionsGrouped();

        this.totalTiles = total;
        this.removedCount = removed_count ?? 0;

        if (!total) {
            this.mountEmptyState();
            return;
        }

        await this.createGroups(groups);

        this.setGridScrollHeight();
    },

    fillTilesState(segments, sectionId = null) {
        return new Promise((resolve) => {
            segments.forEach((segment) => {
                segment.images.forEach((image, imageIndex) => {
                    this.tilesState[`${segment.segmentId}${imageIndex + 1}`] = this.tileStateObject(
                        image,
                        sectionId,
                        segment.segmentId,
                    );
                });
            });

            this.syncRangesToLoadedTiles(this.tilesState);

            resolve();
        });
    },

    initHandleScroll() {
        this.scrollDirection = 'down';
        let lastScrollY = this.container.scrollY;
        let lastTime = Date.now();
        let scrollTimeout;

        const calculateDelay = (speed) => {
            return Math.max(20, Math.min(100, speed * 100));
        };

        const handleScrollStop = () => {
            this.loadSectionData();
        };

        const removeOldSections = () => {
            let sectionsDivs = this.gridScrollNode.querySelectorAll('div.section');

            while (sectionsDivs.length > this.config.removeOldSectionAfter) {
                const indexToRemove = this.scrollDirection === 'down' ? 0 : sectionsDivs.length - 1;

                const sectionToRemove = sectionsDivs[indexToRemove];
                const sectionId = this.splitId(sectionToRemove.id);

                this.cancelPendingImageRequests(sectionToRemove);

                this.sections[sectionId].shouldMountEmptyState = true;
                this.sections[sectionId].shouldUpdate = true;

                this.gridScrollNode.removeChild(sectionToRemove);
                sectionsDivs = this.gridScrollNode.querySelectorAll('div.section');
            }
        };

        const reorderGrid = () => {
            const gridItems = Array.from(this.gridScrollNode.children);

            gridItems.sort((a, b) => {
                const idA = parseInt(a.id.replace(/\D/g, ''), 10);
                const idB = parseInt(b.id.replace(/\D/g, ''), 10);

                return idA - idB;
            });

            gridItems.forEach((item) => this.gridScrollNode.appendChild(item));
        };

        this._scrollAbortController = new AbortController();

        this.container.addEventListener(
            'scroll',
            async () => {
                const currentY = this.container.scrollTop;
                const distance = Math.abs(currentY - lastScrollY);
                this.scrollDirection = currentY > lastScrollY ? 'down' : 'up';

                const currentTime = Date.now();
                const timeDiff = currentTime - lastTime || 1;
                const speed = distance / timeDiff;

                lastScrollY = currentY;
                lastTime = currentTime;

                const delay = calculateDelay(speed);

                clearTimeout(scrollTimeout);
                scrollTimeout = setTimeout(handleScrollStop, delay);

                await this.loadSectionEmpty();
                removeOldSections();
            },
            { passive: true, signal: this._scrollAbortController.signal },
        );
    },

    reset() {
        this._scrollAbortController?.abort();
        this._scrollAbortController = null;

        this.selectedTiles = [];
        this.storageData = null;
        this.storageData = {};
        this.allSelected = false;
        this.totalTiles = 0;
        this.totalTilesCount = 0;
        this.tilesState = {};
        this.lastSelectedTileIndex = null;
        this.selectionIdRanges = [];
        this.rangesCount = 0;
        this.shiftWasPressed = false;
        this.unselectedTiles = [];
        this.unselectedAfterShiftSelect = [];
        this.fetchingImages = false;
        this.sections = null;
        this.sections = [];
        this.sectionSize = 0;
        this.segmentSize = 0;
        this.gridScrollNode = null;
        this.debounceResize = null;
        this.debounceRender = null;
        this.scrollDirection = null;
        this.container = null;

        document.getElementById(this.options.gridContainerId).innerHTML = '';
    },

    getSectionsGrouped() {
        try {
            const params = {
                ...this.params,
                section_size: Number(this.sectionSize) || 6,
                removed: this.options.removed ? 1 : 0,
            };

            return fetch(`/photo-view/${this.route}/sections?` + new URLSearchParams(params)).then(
                (res) => {
                    return res.json();
                },
            );
        } catch (error) {
            console.error('There was a problem with the fetch operation:', error);
        }
    },

    getImageIdsFromSegmentsAndSections(startSectionId, endSectionId, startIndex, endIndex) {
        const { offset, limit } = this.calculateSelectionParams(
            startSectionId,
            endSectionId,
            startIndex,
            endIndex,
        );

        const params = {
            ...this.params,
            offset,
            limit,
            sort: this.options.sort,
            removed: this.options.removed ? 1 : 0,
        };

        try {
            return fetch(
                `/photo-view/quality-control/image-ids?` + new URLSearchParams(params),
            ).then((res) => {
                return res.json();
            });
        } catch (error) {
            console.error('There was a problem while fetching image IDs:', error);
        }
    },

    getSegments(sectionIndex) {
        const sort = this.options.sort;

        const sectionId = this.splitId(sectionIndex);

        const sectionData = this.storageData[`section-${sectionId}`];

        if (sectionData) {
            return Promise.resolve(JSON.parse(sectionData));
        }

        try {
            const params = {
                ...this.params,
                section_size: Number(this.sections[sectionId].sectionSize) || 6,
                offset: this.sections[sectionId].previousSectionSize,
                segment_size: this.segmentSize ?? 6,
                section: this.splitId(sectionIndex),
                sort: sort,
                removed: this.options.removed ? 1 : 0,
            };

            return fetch(`/photo-view/${this.route}/segments?` + new URLSearchParams(params))
                .then((res) => res.json())
                .then((data) => {
                    this.storageData[`section-${sectionId}`] = JSON.stringify(data);

                    this.fillTilesState(data, sectionId);

                    return data;
                });
        } catch (error) {
            console.error('There was a problem with the fetch operation:', error);
        }
    },

    addSelectClick(sectionDiv) {
        sectionDiv.querySelectorAll('div[data-tile-id]').forEach((el) => {
            el.addEventListener('click', (event) =>
                this.selectTile(el.dataset.tileId, event.shiftKey),
            );

            const trashBtn = el.querySelector('button[title="Remove photo"]');
            if (trashBtn) {
                trashBtn.addEventListener('click', (event) => {
                    event.stopPropagation();
                    this.singleAction(el, false);
                });
            }

            const undoRemoveBtn = el.querySelector('button[title="Undo remove"]');
            if (undoRemoveBtn) {
                undoRemoveBtn.addEventListener('click', (event) => {
                    event.stopPropagation();
                    this.singleAction(el, true);
                });
            }

            const restoreBtn = el.querySelector('button[title="Restore photo"]');
            if (restoreBtn) {
                restoreBtn.addEventListener('click', (event) => {
                    event.stopPropagation();
                    this.singleAction(el, true);
                });
            }

            const undoRestoreBtn = el.querySelector('button[title="Undo restore"]');
            if (undoRestoreBtn) {
                undoRestoreBtn.addEventListener('click', (event) => {
                    event.stopPropagation();
                    this.singleAction(el, false);
                });
            }

            const highResLink = el.querySelector('a[id^="high-"]');
            if (highResLink) {
                highResLink.addEventListener('click', (event) => {
                    event.stopPropagation();
                });
            }
        });
    },

    async selectTile(tileIndex, shiftPressed) {
        if (!this.options.selectable) {
            return;
        }

        if (
            shiftPressed &&
            this.lastSelectedTileIndex !== null &&
            this.lastSelectedTileIndex !== undefined &&
            this.options.sort === 'name'
        ) {
            const start = Math.min(this.lastSelectedTileIndex, tileIndex);
            const end = Math.max(this.lastSelectedTileIndex, tileIndex);

            const startTile = this.tilesState[start];
            const endTile = this.tilesState[end];

            if (!startTile || !endTile) return;

            const startSegmentId = parseInt(startTile.segmentId);
            const endSegmentId = parseInt(endTile.segmentId);
            const startSectionId = parseInt(startTile.sectionId);
            const endSectionId = parseInt(endTile.sectionId);

            this.selectTilesInRange(start, end);

            this.updateSelectedTiles();

            if (startSegmentId !== endSegmentId) {
                await this.selectTilesAcrossSegments(start, end, startSectionId, endSectionId);
            }

            this.updateTotalTilesCount();

            this.lastSelectedTileIndex = tileIndex;

            this.shiftWasPressed = true;

            return;
        }

        let tile = this.tilesState[`${tileIndex}`];

        if (tile?.singleActioned) {
            return;
        }

        if (tile) {
            tile.selected = !tile.selected;

            this.updateTileVisualState(tileIndex, tile.selected);

            if (!tile.selected) {
                this.selectedTiles = this.selectedTiles.filter((id) => id != tile.id);

                if (this.allSelected && !this.unselectedTiles.includes(tile.id)) {
                    this.unselectedTiles.push(tile.id);
                }

                if (this.shiftWasPressed && !this.unselectedAfterShiftSelect.includes(tile.id)) {
                    this.unselectedAfterShiftSelect.push(tile.id);
                }

                if (!this.allSelected) {
                    this.allSelected = false;
                }

                if (this.totalTilesCount > 0) {
                    this.totalTilesCount--;
                }
            } else {
                if (!this.selectedTiles.includes(tile.id)) {
                    this.selectedTiles.push(tile.id);
                }

                if (this.allSelected) {
                    this.unselectedTiles = this.unselectedTiles.filter((id) => id !== tile.id);
                    this.unselectedAfterShiftSelect = this.unselectedAfterShiftSelect.filter(
                        (id) => id !== tile.id,
                    );
                }

                this.totalTilesCount++;

                if (this.totalTilesCount === this.totalTiles && this.totalTiles !== 0) {
                    this.allSelected = true;
                }
            }
        }

        this.lastSelectedTileIndex = tileIndex;
    },

    updateTileVisualState(tileIndex, selected) {
        const el = this.container?.querySelector(`[data-tile-id="${tileIndex}"]`);

        if (!el) {
            return;
        }

        el.classList.toggle('border-green-500', selected);
        el.classList.toggle('border-transparent', !selected);

        this.updateSingleActionButton(el, selected);

        const checkbox = el.querySelector('input[type="checkbox"]');

        if (checkbox) {
            checkbox.checked = selected;
        }
    },

    selectTilesInRange(start, end) {
        for (let i = start; i <= end; i++) {
            let tile = this.tilesState[i];

            if (tile && !tile.singleActioned) {
                tile.selected = true;
                this.updateTileVisualState(i, true);
            }
        }
    },

    async updateAfterAction(action, turnTo = true) {
        if (!this.totalTilesCount) {
            return;
        }

        const unselectedTiles = new Set([
            ...this.unselectedTiles,
            ...this.unselectedAfterShiftSelect,
        ]);

        if (turnTo) {
            await this.$wire.deleteImages(
                this.selectedTiles,
                this.allSelected,
                this.selectionIdRanges,
                [...unselectedTiles],
            );
        } else {
            this._suppressGridReload = true;

            await this.$wire.restoreImages(
                this.selectedTiles,
                this.allSelected,
                this.selectionIdRanges,
                [...unselectedTiles],
            );

            if (this.options.removed) {
                await this.$wire.toggleRemoved();

                return;
            }

            this._suppressGridReload = false;
        }
    },

    async singleDeleteImage(imageId, tileEl) {
        this._suppressGridReload = true;
        await this.$wire.deleteImages([imageId], false, [], []);

        this.removedCount++;
        this.markTileActioned(imageId);

        if (tileEl) {
            tileEl.classList.add('opacity-50');
            this.hideAllActionButtons(tileEl);
            this.disableTileInteraction(tileEl);
        }
    },

    async singleRestoreImage(imageId, tileEl) {
        this._suppressGridReload = true;
        await this.$wire.restoreImages([imageId], false, [], []);

        if (this.removedCount > 0) {
            this.removedCount--;
        }

        this.markTileActioned(imageId);

        if (tileEl) {
            tileEl.classList.add('opacity-50');
            this.hideAllActionButtons(tileEl);
            this.disableTileInteraction(tileEl);
        }
    },

    markTileActioned(imageId) {
        const entry = Object.entries(this.tilesState).find(([, tile]) => tile?.id === imageId);
        if (entry) {
            entry[1].singleActioned = true;
            entry[1].selected = false;
        }
    },

    singleAction(el, isRestore = false) {
        const tileIndex = el.dataset.tileId;
        if (!tileIndex) {
            return;
        }

        const tile = this.tilesState[`${tileIndex}`];
        const imageId = tile?.id ?? parseInt(el.dataset.imageId, 10);

        const title = isRestore
            ? 'Do you want to restore this photo for this customer?'
            : 'Do you want to remove this photo from this customer?';

        window.$wireui.confirmDialog({
            title,
            accept: {
                color: 'negative',
                label: 'Yes',
                execute: () => {
                    if (isRestore) {
                        this.singleRestoreImage(imageId, el);
                    } else {
                        this.singleDeleteImage(imageId, el);
                    }
                },
            },
        });
    },

    hideAllActionButtons(el) {
        ['Remove photo', 'Undo remove', 'Restore photo', 'Undo restore'].forEach((title) => {
            const btn = el.querySelector(`button[title="${title}"]`);
            if (btn) {
                btn.classList.add('hidden');
            }
        });
    },

    disableTileInteraction(el) {
        el.classList.add('cursor-not-allowed');

        const checkbox = el.querySelector('input[type="checkbox"]');
        if (checkbox) {
            checkbox.disabled = true;
            checkbox.closest('div')?.classList.add('pointer-events-none');
        }
    },

    updateSingleActionButton(el, selected) {
        const trashBtn = el.querySelector('button[title="Remove photo"]');
        const undoRemoveBtn = el.querySelector('button[title="Undo remove"]');
        const restoreBtn = el.querySelector('button[title="Restore photo"]');
        const undoRestoreBtn = el.querySelector('button[title="Undo restore"]');

        if (selected) {
            if (trashBtn) trashBtn.classList.add('hidden');
            if (undoRemoveBtn) undoRemoveBtn.classList.add('hidden');
            if (restoreBtn) restoreBtn.classList.add('hidden');
            if (undoRestoreBtn) undoRestoreBtn.classList.add('hidden');
        } else {
            if (trashBtn) trashBtn.classList.remove('hidden');
            if (undoRemoveBtn) undoRemoveBtn.classList.add('hidden');
            if (restoreBtn) restoreBtn.classList.remove('hidden');
            if (undoRestoreBtn) undoRestoreBtn.classList.add('hidden');
        }
    },

    selectAll() {
        this.allSelected = true;

        let singleActionedCount = 0;

        Object.entries(this.tilesState).forEach(([index, tile]) => {
            if (!tile?.id) {
                return;
            }

            if (tile.singleActioned) {
                singleActionedCount++;
                return;
            }

            if (!tile.selected) {
                tile.selected = true;
                this.updateTileVisualState(index, true);
            }
        });

        this.totalTilesCount = this.totalTiles - singleActionedCount;
        this.selectedTiles = [];
        this.unselectedTiles = [];
        this.lastSelectedTileIndex = null;
        this.rangesCount = 0;
        this.selectionIdRanges = [];
        this.shiftWasPressed = false;
        this.unselectedAfterShiftSelect = [];
    },

    deselectAll() {
        Object.entries(this.tilesState).forEach(([index, tile]) => {
            if (tile?.id && tile.selected) {
                tile.selected = false;
                this.updateTileVisualState(index, false);
            }
        });

        this.allSelected = false;
        this.selectedTiles = [];
        this.unselectedTiles = [];
        this.lastSelectedTileIndex = null;
        this.rangesCount = 0;
        this.selectionIdRanges = [];
        this.shiftWasPressed = false;
        this.unselectedAfterShiftSelect = [];
        this.totalTilesCount = 0;
    },

    getTileElement(image, index) {
        const tileDiv = this.tileTemplate.cloneNode(true);

        tileDiv.style.width = `${this.tileSize.width}px`;
        tileDiv.style.height = `${this.tileSize.height}px`;

        const isDeletedTile = !this.options.removed && !!image.deleted_at;

        tileDiv.classList.add(isDeletedTile ? 'bg-gray-300' : 'bg-gray-700');
        tileDiv.classList.add('border-transparent');

        if (isDeletedTile) {
            tileDiv.classList.add('opacity-50');
        }

        tileDiv.removeAttribute('x-bind:class');

        tileDiv.setAttribute('data-tile-id', index);
        tileDiv.setAttribute('data-image-id', image.id);

        const img = tileDiv.querySelector('img');
        img.src = image.thumb;
        img.setAttribute('data-image-id', image.id);

        if (this.options.preview) {
            img.setAttribute('x-tooltip.placement.left-start', this.fillPreviewMode(image));
        }

        tileDiv.insertAdjacentHTML('beforeend', this.fillActionsAndTags(image, index));

        const tile = this.tilesState[`${index}`];

        if (isDeletedTile || tile?.singleActioned) {
            this.hideAllActionButtons(tileDiv);
            this.disableTileInteraction(tileDiv);
        } else if (tile?.selected) {
            this.hideAllActionButtons(tileDiv);
        }

        if (tile?.selected) {
            tileDiv.classList.replace('border-transparent', 'border-green-500');
        }

        return tileDiv;
    },

    topImageItems(image, index) {
        const selectCheckbox = this.options.selectable
            ? `
            <div class="absolute top-2 right-2 h-6 w-6 text-center bg-white border-none rounded-full shadow-soft block" x-data>
                <input
                    type="checkbox"
                    class="h-4 w-4 rounded-full p-1 border-2 border-gray-300 text-green-600 form-checkbox focus:ring-green-500"
                    :checked="isSelected('${index}')"
                />
            </div>
        `
            : '';

        const highResLink = this.options.showHighResLink
            ? `
            <a
                href="${image.high_res}"
                target="_blank"
                rel="noopener noreferrer"
                x-on:click="event.stopPropagation();"
                class="cursor-pointer rounded-full p-1 button hover:opacity-75 bg-gray-100"
                id="high-${index}">
                <svg class="w-5 h-5 text-blue-500" x-ref="previewIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
                </svg>
            </a>
        `
            : '';

        const trashButton = !this.options.removed
            ? `
            <button
                class="cursor-pointer rounded-full p-1 button hover:opacity-75 bg-gray-100"
                title="Remove photo">
                <svg class="w-5 h-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
            </button>
            <button
                class="hidden cursor-pointer rounded-full p-1 button hover:opacity-75 bg-gray-100"
                title="Undo remove">
                <svg class="w-5 h-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/>
                </svg>
            </button>
        `
            : '';

        const restoreButton = this.options.removed
            ? `
            <button
                class="cursor-pointer rounded-full p-1 button hover:opacity-75 bg-gray-100"
                title="Restore photo">
                <svg class="w-5 h-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/>
                </svg>
            </button>
            <button
                class="hidden cursor-pointer rounded-full p-1 button hover:opacity-75 bg-gray-100"
                title="Undo restore">
                <svg class="w-5 h-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
            </button>
        `
            : '';

        return `
            <div class="top-buttons buttons absolute top-0 left-0 w-full h-10 flex items-center justify-between px-2">
              ${selectCheckbox}
              <div class="flex gap-1">
                ${trashButton}${restoreButton}${highResLink}
              </div>
            </div>
        `;
    },
});
